import React from 'react';
import PropTypes from 'prop-types';
import Ratings from "../Ratings/index.js";
import t from 'format-message';
import { algorithmDataShape, scoringTierShape } from "../../store/shapes.js";
import { HIGHEST, LATEST, N_MASTERY, DECAYING_AVERAGE, AVERAGE, CALCULATION_METHODS, WEIGHTED_AVERAGE, STANDARD_DECAYING_AVERAGE, NEW_DECAYING_AVERAGE_FF } from "../../constants.js";
import { Flex } from '@instructure/ui-flex';
import { Text } from '@instructure/ui-text';
import { TruncateText } from '@instructure/ui-truncate-text';
import { View } from '@instructure/ui-view';
var OutcomeDescription = function OutcomeDescription(_ref) {
  var label = _ref.label,
    description = _ref.description,
    friendlyDescription = _ref.friendlyDescription,
    shouldTruncateText = _ref.shouldTruncateText,
    maxLines = _ref.maxLines,
    calculationMethod = _ref.calculationMethod,
    calculationInt = _ref.calculationInt,
    masteryPercent = _ref.masteryPercent,
    pointsPossible = _ref.pointsPossible,
    ratings = _ref.ratings,
    truncated = _ref.truncated,
    isTray = _ref.isTray,
    canManageOutcomes = _ref.canManageOutcomes,
    features = _ref.features;
  var newDecayingAvgFFEnabled = features.includes(NEW_DECAYING_AVERAGE_FF);
  var descriptionDisplay = !canManageOutcomes && friendlyDescription ? friendlyDescription : description;
  var stripHtmlTags = function stripHtmlTags(text) {
    var doc = new DOMParser().parseFromString(text, 'text/html');
    return doc.body.textContent || '';
  };
  var truncateText = function truncateText(text, position) {
    return /*#__PURE__*/React.createElement(TruncateText, {
      maxLines: maxLines,
      position: position
    }, text);
  };
  var renderText = function renderText(text, position) {
    var strippedText = stripHtmlTags(text);
    var renderedText = truncated && shouldTruncateText ? truncateText(strippedText, position) : strippedText;
    return /*#__PURE__*/React.createElement(Text, null, renderedText);
  };
  var renderUserFacingMethod = function renderUserFacingMethod(calculationMethod) {
    var displayMethod = DECAYING_AVERAGE;
    var percent = calculationInt.decaying_average_percent;
    if (newDecayingAvgFFEnabled) {
      switch (calculationMethod) {
        case DECAYING_AVERAGE:
          {
            displayMethod = WEIGHTED_AVERAGE;
            percent = calculationInt.decaying_average_percent;
            break;
          }
        case STANDARD_DECAYING_AVERAGE:
          {
            displayMethod = STANDARD_DECAYING_AVERAGE;
            percent = calculationInt.standard_decaying_average_percent;
            break;
          }
      }
    }
    var complement = 100 - percent;
    return t(CALCULATION_METHODS[displayMethod], {
      percent: percent,
      complement: complement
    });
  };
  var formatCalculationMethodText = function formatCalculationMethodText() {
    var method = '';
    switch (calculationMethod) {
      case HIGHEST:
        {
          method = t(CALCULATION_METHODS[HIGHEST]);
          break;
        }
      case LATEST:
        {
          method = t(CALCULATION_METHODS[LATEST]);
          break;
        }
      case N_MASTERY:
        {
          var n = calculationInt.n_mastery_count;
          method = t(CALCULATION_METHODS[N_MASTERY], {
            n: n
          });
          break;
        }
      case AVERAGE:
        {
          method = t(CALCULATION_METHODS[AVERAGE]);
          break;
        }
      case DECAYING_AVERAGE:
      case STANDARD_DECAYING_AVERAGE:
        {
          method = renderUserFacingMethod(calculationMethod);
          break;
        }
    }
    return method;
  };
  var renderFriendlyDescription = function renderFriendlyDescription() {
    return friendlyDescription && /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(View, {
      as: "div",
      margin: "x-small small 0 0",
      padding: "small small x-small small",
      background: "secondary",
      "data-automation": "outcomeDescription__friendly_description_header"
    }, /*#__PURE__*/React.createElement(Text, {
      weight: "bold"
    }, t('Friendly Description'))), /*#__PURE__*/React.createElement(View, {
      as: "div",
      margin: "0 small x-small 0",
      padding: "0 small small small",
      background: "secondary",
      "data-automation": "outcomeDescription__friendly_description_expanded"
    }, /*#__PURE__*/React.createElement(Text, {
      wrap: "break-word"
    }, friendlyDescription)));
  };
  var renderRatingsAndCalculation = function renderRatingsAndCalculation() {
    var calculationMethodRequiringCalculationInt = [N_MASTERY, STANDARD_DECAYING_AVERAGE, WEIGHTED_AVERAGE, DECAYING_AVERAGE];
    var hasRequireData = calculationMethodRequiringCalculationInt.includes(calculationMethod) ? calculationInt : true;
    return /*#__PURE__*/React.createElement(View, null, ratings.length > 0 && /*#__PURE__*/React.createElement(View, {
      as: "div",
      margin: "small none"
    }, /*#__PURE__*/React.createElement(Ratings, {
      ratings: ratings,
      masteryPercent: masteryPercent,
      pointsPossible: pointsPossible,
      isTray: isTray
    })), calculationMethod && hasRequireData && /*#__PURE__*/React.createElement(Flex, {
      wrap: "wrap",
      padding: "none small small none"
    }, /*#__PURE__*/React.createElement(Flex.Item, {
      as: "div",
      padding: "none xx-small none none"
    }, /*#__PURE__*/React.createElement(View, {
      as: "div"
    }, /*#__PURE__*/React.createElement(Text, {
      weight: "bold"
    }, t('Proficiency Calculation:')))), /*#__PURE__*/React.createElement(Flex.Item, null, /*#__PURE__*/React.createElement(Text, {
      color: "primary",
      weight: "normal"
    }, formatCalculationMethodText()))));
  };
  return /*#__PURE__*/React.createElement("div", null, label && canManageOutcomes && /*#__PURE__*/React.createElement(View, {
    as: "div",
    margin: "0 0 xx-small"
  }, renderText(label, 'middle')), !truncated && canManageOutcomes && renderFriendlyDescription(), description && /*#__PURE__*/React.createElement(View, {
    as: "div",
    margin: canManageOutcomes ? 'xx-small 0' : '0',
    "data-automation": "outcomeDescription__description"
  }, renderText(descriptionDisplay, 'end')), !truncated && renderRatingsAndCalculation());
};
OutcomeDescription.propTypes = {
  label: PropTypes.string,
  description: PropTypes.string,
  friendlyDescription: PropTypes.string,
  shouldTruncateText: PropTypes.bool,
  maxLines: PropTypes.number,
  calculationMethod: PropTypes.string,
  calculationInt: algorithmDataShape,
  masteryPercent: PropTypes.number,
  pointsPossible: PropTypes.number,
  ratings: PropTypes.arrayOf(scoringTierShape),
  truncated: PropTypes.bool,
  isTray: PropTypes.bool,
  canManageOutcomes: PropTypes.bool,
  features: PropTypes.array
};
OutcomeDescription.defaultProps = {
  label: '',
  description: '',
  friendlyDescription: '',
  shouldTruncateText: true,
  maxLines: 1,
  isTray: false,
  calculationMethod: '',
  calculationInt: null,
  masteryPercent: null,
  pointsPossible: null,
  ratings: [],
  truncated: true,
  canManageOutcomes: true,
  features: []
};
export default OutcomeDescription;